<template>
  <div class="meu-agente-operacao">
    <b-overlay
      :show="loadingOverlay"
      rounded="sm"
    >
      <!-- Seletor de Corretora -->
      <div
        v-if="Object.keys(availableExchanges).length > 1"
        class="exchange-selector-container mb-4"
      >
        <div class="exchange-selector-header">
          <div class="header-content">
            <div class="header-title">
              <feather-icon
                icon="LayersIcon"
                class="header-icon"
              />
              <div>
                <h5 class="title-text">
                  {{ $t('spot.selectExchange') }}
                </h5>
                <small class="title-subtitle">
                  {{ $t('spot.meuAgente.selecioneCorretoraParaConfigurar') }}
                </small>
              </div>
            </div>
          </div>
        </div>
        <div class="exchange-selector-content">
          <div
            class="exchange-pills"
            role="radiogroup"
            :aria-label="$t('spot.selectExchange')"
          >
            <button
              v-for="(exchange, key) in availableExchanges"
              :key="key"
              ref="exchangePill"
              class="exchange-pill"
              :class="{ 'active': selectedExchange === key }"
              type="button"
              role="radio"
              :aria-checked="selectedExchange === key ? 'true' : 'false'"
              :aria-label="exchange.name"
              :tabindex="selectedExchange === key ? 0 : -1"
              :style="{
                '--pill-color-start': exchange.color,
                '--pill-color-end': lightenColor(exchange.color, 12)
              }"
              @click="selectExchange(key)"
            >
              <img
                :src="require(`@/assets/images/logo/${key}-logo.png`)"
                :alt="exchange.name"
                class="pill-logo"
              >
              <div class="pill-info">
                <span class="pill-text">{{ exchange.name }}</span>
              </div>
              <div
                v-if="selectedExchange === key"
                class="active-indicator"
              />
              <b-spinner
                v-if="selectedExchange === key && loadingOverlay"
                small
                class="pill-spinner"
                label="loading"
              />
            </button>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <b-alert
        v-if="errorMessage"
        variant="danger"
        show
        class="mb-4"
      >
        <div class="alert-body">
          <feather-icon
            icon="AlertTriangleIcon"
            class="mr-50"
          />
          <span v-html="errorMessage" />
        </div>
      </b-alert>

      <template v-else>
        <!-- Dashboard de Saldos Melhorado -->
        <b-card
          no-body
          class="saldo-dashboard-card mb-4"
        >
          <b-card-header class="dashboard-header">
            <div class="header-content">
              <div class="header-title">
                <feather-icon
                  icon="PieChartIcon"
                  class="header-icon"
                />
                <div>
                  <h5 class="title-text">
                    {{ $t('spot.meuAgente.titleSaldo') }}
                  </h5>
                  <small class="title-subtitle">
                    {{ $t('spot.meuAgente.resumoInvestimentos') }}
                  </small>
                </div>
              </div>
              <div class="header-badge">
                <b-badge
                  v-if="selectedExchange"
                  :variant="getExchangeBadgeVariant(selectedExchange)"
                >
                  {{ selectedExchange.toUpperCase() }}
                </b-badge>
              </div>
            </div>
          </b-card-header>

          <b-card-body class="p-0">
            <!-- Grid de Cards de Saldo -->
            <div class="saldo-grid">
              <div
                v-for="resumo in filteredResumoSaldos"
                :key="resumo.paridade"
                class="saldo-card-modern"
                :class="getParidadeCardClass(resumo.paridade)"
              >
                <!-- Header do Card -->
                <div class="card-header-modern">
                  <div class="currency-info">
                    <div class="currency-icon">
                      <b-avatar
                        :src="getIcon(resumo.paridade)"
                        size="32px"
                        class="currency-avatar"
                      />
                    </div>
                    <div class="currency-details">
                      <h6 class="currency-name">
                        {{ resumo.paridade }}
                      </h6>
                      <span class="currency-total">{{ resumo.total }}</span>
                    </div>
                  </div>
                  <div class="card-status">
                    <b-badge
                      :variant="resumo.perfis.length > 0 ? 'success' : 'warning'"
                      class="status-badge"
                    >
                      {{ resumo.perfis.length > 0 ? $t('spot.meuAgente.ativo') : $t('spot.meuAgente.inativo') }}
                    </b-badge>
                  </div>
                </div>

                <!-- Conteúdo do Card -->
                <div class="card-content-modern">
                  <!-- Estado Inativo -->
                  <div
                    v-if="resumo.perfis.length === 0"
                    class="inactive-state"
                  >
                    <div class="inactive-icon">
                      <feather-icon
                        icon="PauseCircleIcon"
                        size="24"
                      />
                    </div>
                    <div class="inactive-text">
                      <p class="inactive-title">
                        {{ $t('spot.meuAgente.investimentosPausados') }}
                      </p>
                      <small class="inactive-description">
                        {{ $t('spot.meuAgente.ativeParaComecarOperar', { paridade: resumo.paridade }) }}
                      </small>
                    </div>
                  </div>

                  <!-- Estado Ativo -->
                  <div
                    v-for="item in resumo.perfis"
                    :key="item.perfil"
                    class="active-state"
                  >

                    <!-- Métricas em Cards Compactos -->
                    <div class="metrics-compact">
                      <!-- Valor por Operação -->
                      <div class="metric-compact metric-operation">
                        <div class="metric-header">
                          <feather-icon
                            icon="ShoppingCartIcon"
                            size="14"
                          />
                          <span class="metric-name">{{ $t('spot.meuAgente.porOperacao') }}</span>
                        </div>
                        <div class="metric-data">
                          <span
                            class="metric-value"
                            v-html="item.operacao"
                          />
                          <div class="metric-progress">
                            <div
                              class="progress-bar operation-progress"
                              :style="{ width: item.operacaoPerc + '%' }"
                            />
                          </div>
                        </div>
                      </div>

                      <!-- Disponível -->
                      <div class="metric-compact metric-available">
                        <div class="metric-header">
                          <feather-icon
                            icon="CheckCircleIcon"
                            size="14"
                          />
                          <span class="metric-name">{{ $t('spot.meuAgente.disponivel') }}</span>
                        </div>
                        <div class="metric-data">
                          <span class="metric-value">{{ item.livre }}</span>
                          <div class="metric-progress">
                            <div
                              class="progress-bar available-progress"
                              :style="{ width: item.livrePerc + '%' }"
                            />
                          </div>
                        </div>
                      </div>

                      <!-- Em Uso -->
                      <div class="metric-compact metric-used">
                        <div class="metric-header">
                          <feather-icon
                            icon="TrendingUpIcon"
                            size="14"
                          />
                          <span class="metric-name">{{ $t('spot.meuAgente.emUso') }}</span>
                        </div>
                        <div class="metric-data">
                          <span class="metric-value">{{ item.bloqueado }}</span>
                          <div class="metric-progress">
                            <div
                              class="progress-bar used-progress"
                              :style="{ width: item.bloqueadoPerc + '%' }"
                            />
                          </div>
                        </div>
                      </div>

                      <!-- Total -->
                      <div class="metric-compact metric-total">
                        <div class="metric-header">
                          <feather-icon
                            icon="DollarSignIcon"
                            size="14"
                          />
                          <span class="metric-name">
                            {{ item.limitaSaldo ? $t('spot.meuAgente.totalLimitado') : $t('spot.meuAgente.totalGeral') }}
                          </span>
                        </div>
                        <div class="metric-data">
                          <span class="metric-value metric-value-total">{{ item.total }}</span>
                          <div class="metric-progress">
                            <div
                              class="progress-bar total-progress"
                              :style="{ width: item.totalPerc + '%' }"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Mensagem quando não há saldos -->
            <div
              v-if="filteredResumoSaldos.length === 0"
              class="empty-state"
            >
              <div class="empty-icon">
                <feather-icon
                  icon="AlertCircleIcon"
                  size="48"
                />
              </div>
              <div class="empty-content">
                <h6 class="empty-title">
                  {{ $t('spot.meuAgente.nenhumSaldoEncontrado') }}
                </h6>
                <p class="empty-description">
                  {{ $t('spot.meuAgente.selecioneCorretoraSaldos') }}
                </p>
              </div>
            </div>
          </b-card-body>
        </b-card>

        <!-- Configurações por Perfil -->
        <b-card
          no-body
          class="configuracao-card"
        >
          <b-card-header>
            <div class="header-content">
              <div class="header-title">
                <feather-icon
                  icon="SettingsIcon"
                  class="mr-50"
                />
                <div>
                  <h5 class="title-text">
                    {{ $t('spot.meuAgente.titleConfiguracoes') }}
                  </h5>
                  <small class="title-subtitle">
                    {{ $t('spot.meuAgente.configureParidadesOperar') }}
                  </small>
                </div>
              </div>
              <div class="header-badge">
                <b-badge
                  v-if="selectedExchange"
                  :variant="getExchangeBadgeVariant(selectedExchange)"
                >
                  {{ selectedExchange.toUpperCase() }}
                </b-badge>
              </div>
            </div>
          </b-card-header>
          <b-card-body>
            <!-- Mensagem quando não há configurações -->
            <b-alert
              v-if="!currentConfig && selectedExchange"
              variant="info"
              show
              class="mb-4"
            >
              <div class="alert-body">
                <feather-icon
                  icon="InfoIcon"
                  class="mr-50"
                />
                {{ $t('spot.meuAgente.nenhumaConfiguracaoCorretora', { corretora: selectedExchange.toUpperCase() }) }}
              </div>
            </b-alert>

            <validation-observer ref="simpleRules">
              <b-form>
                <div
                  v-if="currentConfig"
                  class="configuration-content"
                >

                  <!-- Seção de Ativação -->
                  <div class="config-section">
                    <div class="section-header">
                      <feather-icon
                        icon="PowerIcon"
                        class="section-icon"
                      />
                      <div>
                        <h5 class="section-title">
                          {{ $t('spot.meuAgente.primeiroPasso') }}
                        </h5>
                        <p class="section-subtitle">
                          {{ currentConfig.status=='Desligado' ? $t('spot.meuAgente.cliqueSimInvestir') : $t('spot.meuAgente.cliqueNaoPausar') }}
                        </p>
                      </div>
                    </div>

                    <div class="config-content">
                      <b-form-radio-group
                        v-model="currentConfig.status"
                        class="status-radio-group"
                      >
                        <b-form-radio
                          value="Ligado"
                          class="custom-control-success status-radio"
                        >
                          <div class="radio-content">
                            <feather-icon
                              icon="CheckCircleIcon"
                              class="radio-icon success"
                            />
                            <span>{{ $t('spot.meuAgente.sim') }}</span>
                          </div>
                        </b-form-radio>
                        <b-form-radio
                          value="Desligado"
                          class="custom-control-danger status-radio"
                        >
                          <div class="radio-content">
                            <feather-icon
                              icon="XCircleIcon"
                              class="radio-icon danger"
                            />
                            <span>{{ $t('spot.meuAgente.nao') }}</span>
                          </div>
                        </b-form-radio>
                      </b-form-radio-group>

                      <!-- Opções Avançadas dentro do Passo 1 -->
                      <div class="advanced-options-section">
                        <app-collapse
                          accordion
                          type="border"
                        >
                          <app-collapse-item :title="$t('spot.meuAgente.titleConfiguracoesAvancadas')">
                            <b-row>
                              <b-col lg="6">
                                <b-row>
                                  <b-col lg="12">
                                    <div>{{ $t('spot.meuAgente.seguirRecomendacaoMoedasAgente') }}</div>
                                    <div class="demo-inline-spacing mb-2">
                                      <b-form-radio-group v-model="currentConfig.aceitaWhiteList">
                                        <b-form-radio
                                          name="aceitaWhiteList"
                                          value="true"
                                        >
                                          {{ $t('spot.meuAgente.sim') }}
                                        </b-form-radio>
                                        <b-form-radio
                                          name="aceitaWhiteList"
                                          value="false"
                                        >
                                          {{ $t('spot.meuAgente.nao') }}
                                        </b-form-radio>
                                      </b-form-radio-group>
                                    </div>

                                    <b-form-group
                                      :label="$t('spot.meuAgente.aceitarSomenteMoedas')"
                                      label-for="aceita-moeda"
                                      :description="$t('spot.meuAgente.deixeEmBrancoAceitarTodasMoedas')"
                                    >
                                      <b-form-textarea
                                        v-if="currentConfig.aceitaWhiteList=='false'"
                                        v-model="currentConfig.whiteList"
                                        @input="currentConfig.whiteList = currentConfig.whiteList.toUpperCase()"
                                      />
                                      <b-form-textarea
                                        v-else
                                        v-model="currentConfig.whiteListAgente"
                                        disabled
                                      />
                                    </b-form-group>
                                  </b-col>
                                </b-row>
                                <b-row>
                                  <b-col lg="12">
                                    <b-form-group
                                      :label="$t('spot.meuAgente.ignorarAsMoedas')"
                                      label-for="ignora-moeda"
                                      :description="$t('spot.meuAgente.deixeEmBrancoNaoIgnorarMoeda')"
                                    >
                                      <b-form-textarea
                                        v-model="currentConfig.blackList"
                                        d:escription="$t('spot.meuAgente.useVirgulaParaSeparar')"
                                        @input="currentConfig.blackList = currentConfig.blackList.toUpperCase()"
                                      />
                                    </b-form-group>
                                  </b-col>
                                </b-row>
                              </b-col>
                              <b-col lg="6">
                                <b-row>
                                  <b-col lg="12">
                                    <b-form-group
                                      :label="$t('spot.meuAgente.cancelarCompraNaoExecutada')"
                                      label-for="cancel-compra"
                                    >
                                      <b-form-select
                                        v-model="currentConfig.stopCompraPorcentagem"
                                        :options="stopOptions(currentConfig)"
                                        input-id="cancel-compra"
                                      />
                                    </b-form-group>
                                  </b-col>
                                  <b-col lg="12">
                                    <b-form-group
                                      :label="$t('spot.meuAgente.tipoVenda')"
                                      label-for="tipo-venda"
                                    >
                                      <b-form-select
                                        v-model="currentConfig.tipoOrdemVenda"
                                        :options="tipoOrdemVendaOptions(currentConfig)"
                                        input-id="tipo-venda"
                                        @change="alteraTipoVenda(currentConfig)"
                                      />
                                    </b-form-group>
                                  </b-col>
                                  <b-col lg="12">
                                    <b-form-group
                                      :label="$t('spot.meuAgente.stoplossMax')"
                                      label-for="stoploss-max"
                                    >
                                      <b-form-select
                                        v-model="currentConfig.stoplossLimit"
                                        :disabled="isTipoVendaLimite(currentConfig)"
                                        :options="stopVendaLimit"
                                        input-id="stoploss-max"
                                      />
                                    </b-form-group>
                                  </b-col>
                                </b-row>
                              </b-col>
                            </b-row>
                          </app-collapse-item>
                        </app-collapse>
                      </div>
                    </div>
                  </div>

                  <!-- Seção de Configuração de Moedas -->
                  <div class="config-section">
                    <div class="section-header">
                      <feather-icon
                        icon="CoinsIcon"
                        class="section-icon"
                      />
                      <div>
                        <h5 class="section-title">
                          {{ $t('spot.meuAgente.segundoPasso') }}
                        </h5>
                        <p class="section-subtitle">
                          {{ $t('spot.meuAgente.escolhaMoedasBases') }}
                        </p>
                      </div>
                    </div>

                    <div class="config-content">
                      <b-tabs
                        content-class="pt-1"
                        justified
                        class="currency-tabs"
                      >
                        <b-tab
                          v-if="isHabilitaParidade('USDT')"
                          title="USDT"
                          title-item-class="currency-tab-item"
                          title-link-class="currency-tab-link"
                        >
                          <b-row class="mb-2">
                            <b-col cols="12">
                              <h5 class="mb-2 text-muted">
                                {{ $t('spot.meuAgente.usdtDescricao') }}
                              </h5>
                              <h5 class="font-medium">
                                {{ $t('spot.meuAgente.ativarUSDTInvestimento') }}
                              </h5>
                              <div class="demo-inline-spacing">
                                <b-form-radio-group v-model="currentConfig.paridadeUSD">
                                  <b-form-radio
                                    name="ativar-usd"
                                    value="true"
                                    class="custom-control-success"
                                  >
                                    {{ $t('spot.meuAgente.sim') }}
                                  </b-form-radio>
                                  <b-form-radio
                                    name="ativar-usd"
                                    value="false"
                                    class="custom-control-danger"
                                  >
                                    {{ $t('spot.meuAgente.nao') }}
                                  </b-form-radio>
                                </b-form-radio-group>
                              </div>
                            </b-col>
                          </b-row>
                          <b-row class="mb-2">
                            <b-col cols="12">
                              <h5 class="font-medium">
                                {{ $t('spot.meuAgente.quantoQuerInvestir') }}
                              </h5>
                              <div class="demo-inline-spacing">
                                <b-form-radio-group v-model="currentConfig.tipoValorUSD">
                                  <b-form-radio
                                    name="tipo-valor-usd"
                                    value="A"
                                    @change="trocaTipoValorUSD"
                                  >
                                    {{ $t('spot.meuAgente.emValor') }}
                                  </b-form-radio>
                                  <b-form-radio
                                    name="tipo-valor-usd"
                                    value="P"
                                    @change="trocaTipoValorUSD"
                                  >
                                    {{ $t('spot.meuAgente.emPorcentagem') }}
                                  </b-form-radio>
                                </b-form-radio-group>
                              </div>
                            </b-col>
                          </b-row>
                          <b-row>
                            <b-col lg="4">
                              <b-form-group
                                label-for="precoMaxOrdemUSD"
                              >
                                <label v-if="currentConfig.tipoValorUSD=='A'">{{ $t('spot.meuAgente.valorPorOperacao') }}</label>
                                <label v-else>{{ $t('spot.meuAgente.percentualPorOperacao') }}</label>
                                <small
                                  v-if="currentConfig.tipoValorUSD=='A'"
                                  class="text-muted"
                                >
                                  <i>  {{ $t('spot.meuAgente.exemploValor') }}</i>
                                </small>
                                <small
                                  v-else
                                  class="text-muted"
                                >
                                  <i>  {{ $t('spot.meuAgente.exemploPercentual') }}</i>
                                </small>
                                <validation-provider
                                  #default="{ errors }"
                                  :rules="{ required: currentConfig.paridadeUSD === 'true', regex: /^[+]?\d+(\.\d+)?$/, min_value: currentConfig.tipoValorUSD==='A' ? 6:0.1, max_value:currentConfig.tipoValorUSD==='A'? 99999 : 99 }"
                                  :name="currentConfig.tipoValorUSD === 'A'? $t('spot.meuAgente.valorPorOperacao'): $t('spot.meuAgente.percentualPorOperacao')"
                                  :custom-messages="{
                                    min_value: currentConfig.tipoValorUSD === 'A'
                                      ? 'O valor deve ser maior ou igual a 6.'
                                      : 'O valor deve ser maior ou igual a 0.1.',
                                  }"
                                >
                                  <b-form-input
                                    id="precoMaxOrdemUSD"
                                    v-model="currentConfig.precoMaxOrdemUSD"
                                    :state="errors.length > 0 ? false: null"
                                  />
                                  <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                              </b-form-group>
                            </b-col>
                          </b-row>
                          <app-collapse
                            accordion
                            type="border"
                          >
                            <app-collapse-item :title="$t('spot.meuAgente.configuracoesAvancadasUSDT')">
                              <b-row>
                                <b-col lg="4">
                                  <b-form-group :description="$t('spot.meuAgente.deixeEmBrancoParaOperar')">
                                    <label v-if="currentConfig.tipoValorUSD=='A'">{{ $t('spot.meuAgente.valorTotalDisponibilizar') }}</label>
                                    <label v-else> {{ $t('spot.meuAgente.percentualTotalDisponibilizar') }}</label>
                                    <small
                                      v-if="currentConfig.tipoValorUSD=='A'"
                                      class="text-muted"
                                    >
                                      <i>  {{ $t('spot.meuAgente.exemploValorTotal') }}</i>
                                    </small>
                                    <small
                                      v-else
                                      class="text-muted"
                                    >
                                      <i>  {{ $t('spot.meuAgente.exemploPercentualTotal') }}</i>
                                    </small>
                                    <validation-provider
                                      #default="{ errors }"
                                      :rules="{ regex: /^[+]?\d+(\.\d+)?$/, min_value: currentConfig.tipoValorUSD==='A' ? 6:0.1, max_value:currentConfig.tipoValorUSD==='A'? 99999 : 99 }"
                                      :name="currentConfig.tipoValorUSD==='A'? $t('spot.meuAgente.valorTotalDisponibilizar'): $t('spot.meuAgente.percentualTotalDisponibilizar')"
                                    >
                                      <b-form-input
                                        id="precoMaxTotalUSD"
                                        v-model="currentConfig.precoMaxTotalUSD"
                                        :state="errors.length > 0 ? false: null"
                                      />
                                      <small class="text-danger">{{ errors[0] }}</small>
                                    </validation-provider>
                                  </b-form-group>
                                </b-col>
                                <b-col lg="12">
                                  <b-row>
                                    <b-col lg="4">
                                      <b-form-group
                                        :label="$t('spot.meuAgente.saida')"
                                        label-for="alvo-usd"
                                      >
                                        <b-form-select
                                          v-model="currentConfig.alvoVendaUSD"
                                          :options="alvosOptionsUSD(currentConfig)"
                                          input-id="alvo-usd"
                                          @change="alteraAlvo('USD',currentConfig)"
                                        />
                                      </b-form-group>
                                    </b-col>
                                  </b-row>
                                  <b-row v-if="currentConfig.alvoVendaUSD=='0'">
                                    <b-col lg="2">
                                      <b-form-group
                                        :label="$t('spot.meuAgente.primeiraSaida')"
                                        label-for="alvo1-usd"
                                      >
                                        <validation-provider
                                          #default="{ errors }"
                                          :rules="{ required: currentConfig.paridadeUSD == 'true' && currentConfig.alvoVendaUSD == '0' }"
                                          name="Primeira saída (%)"
                                        >
                                          <b-form-select
                                            v-model="currentConfig.alvoVendaParcial1USD"
                                            :options="alvosPorcentagemOptions"
                                            input-id="alvo1-usd"
                                            :state="errors.length > 0 ? false: null"
                                          />
                                          <small class="text-danger">{{ errors[0] }}</small>
                                        </validation-provider>
                                      </b-form-group>
                                    </b-col>
                                    <b-col lg="2">
                                      <b-form-group
                                        :label="$t('spot.meuAgente.SegundaSaida')"
                                        label-for="alvo2-usd"
                                      >
                                        <b-form-input
                                          id="alvo2-usd"
                                          disabled
                                          :value="100-currentConfig.alvoVendaParcial1USD"
                                        />
                                      </b-form-group>
                                    </b-col>
                                  </b-row>
                                  <b-row v-if="currentConfig.alvoVendaUSD=='0'">
                                    <b-col lg="6">
                                      <b-alert
                                        show
                                        variant="warning"
                                      >
                                        <div class="alert-body">
                                          <span class="ml-25">{{ $t('spot.meuAgente.avisoAlvoInferior') }}</span>
                                        </div>
                                      </b-alert>
                                    </b-col>
                                  </b-row>
                                </b-col>
                              </b-row>
                            </app-collapse-item>
                          </app-collapse>
                        </b-tab>

                        <b-tab
                          v-if="isHabilitaParidade('USDC')"
                          title="USDC"
                          title-item-class="currency-tab-item"
                          title-link-class="currency-tab-link"
                        >
                          <b-row class="mb-2">
                            <b-col cols="12">
                              <h5 class="mb-2 text-muted">
                                {{ $t('spot.meuAgente.usdcDescricao') }}
                              </h5>
                              <h5 class="font-medium">
                                {{ $t('spot.meuAgente.ativarUSDCInvestimento') }}
                              </h5>
                              <div class="demo-inline-spacing">
                                <b-form-radio-group v-model="currentConfig.paridadeUSDC">
                                  <b-form-radio
                                    name="ativar-usdc"
                                    value="true"
                                    class="custom-control-success"
                                  >
                                    {{ $t('spot.meuAgente.sim') }}
                                  </b-form-radio>
                                  <b-form-radio
                                    name="ativar-usdc"
                                    value="false"
                                    class="custom-control-danger"
                                  >
                                    {{ $t('spot.meuAgente.nao') }}
                                  </b-form-radio>
                                </b-form-radio-group>
                              </div>
                            </b-col>
                          </b-row>
                          <b-row class="mb-2">
                            <b-col cols="12">
                              <h5 class="font-medium">
                                {{ $t('spot.meuAgente.quantoQuerInvestir') }}
                              </h5>
                              <div class="demo-inline-spacing">
                                <b-form-radio-group v-model="currentConfig.tipoValorUSDC">
                                  <b-form-radio
                                    name="tipo-valor-usdc"
                                    value="A"
                                    @change="trocaTipoValorUSDC"
                                  >
                                    {{ $t('spot.meuAgente.emValor') }}
                                  </b-form-radio>
                                  <b-form-radio
                                    name="tipo-valor-usdc"
                                    value="P"
                                    @change="trocaTipoValorUSDC"
                                  >
                                    {{ $t('spot.meuAgente.emPorcentagem') }}
                                  </b-form-radio>
                                </b-form-radio-group>
                              </div>
                            </b-col>
                          </b-row>
                          <b-row>
                            <b-col lg="4">
                              <b-form-group
                                label-for="precoMaxOrdemUSDC"
                              >
                                <label v-if="currentConfig.tipoValorUSDC=='A'">{{ $t('spot.meuAgente.valorPorOperacao') }}</label>
                                <label v-else>{{ $t('spot.meuAgente.percentualPorOperacao') }}</label>
                                <small
                                  v-if="currentConfig.tipoValorUSDC=='A'"
                                  class="text-muted"
                                >
                                  <i>  {{ $t('spot.meuAgente.exemploValor') }}</i>
                                </small>
                                <small
                                  v-else
                                  class="text-muted"
                                >
                                  <i>  {{ $t('spot.meuAgente.exemploPercentual') }}</i>
                                </small>
                                <validation-provider
                                  #default="{ errors }"
                                  :rules="{ required: currentConfig.paridadeUSDC === 'true', regex: /^[+]?\d+(\.\d+)?$/, min_value: currentConfig.tipoValorUSDC==='A' ? 6:0.1, max_value:currentConfig.tipoValorUSDC==='A'? 99999 : 99 }"
                                  :name="currentConfig.tipoValorUSDC === 'A'? $t('spot.meuAgente.valorPorOperacao'): $t('spot.meuAgente.percentualPorOperacao')"
                                  :custom-messages="{
                                    min_value: currentConfig.tipoValorUSDC === 'A'
                                      ? 'O valor deve ser maior ou igual a 6.'
                                      : 'O valor deve ser maior ou igual a 0.1.',
                                  }"
                                >
                                  <b-form-input
                                    id="precoMaxOrdemUSDC"
                                    v-model="currentConfig.precoMaxOrdemUSDC"
                                    :state="errors.length > 0 ? false: null"
                                  />
                                  <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                              </b-form-group>
                            </b-col>
                          </b-row>
                          <app-collapse
                            accordion
                            type="border"
                          >
                            <app-collapse-item :title="$t('spot.meuAgente.configuracoesAvancadasUSDC')">
                              <b-row>
                                <b-col lg="4">
                                  <b-form-group :description="$t('spot.meuAgente.deixeEmBrancoParaOperar')">
                                    <label v-if="currentConfig.tipoValorUSDC=='A'">{{ $t('spot.meuAgente.valorTotalDisponibilizar') }}</label>
                                    <label v-else> {{ $t('spot.meuAgente.percentualTotalDisponibilizar') }}</label>
                                    <small
                                      v-if="currentConfig.tipoValorUSDC=='A'"
                                      class="text-muted"
                                    >
                                      <i>  {{ $t('spot.meuAgente.exemploValorTotal') }}</i>
                                    </small>
                                    <small
                                      v-else
                                      class="text-muted"
                                    >
                                      <i>  {{ $t('spot.meuAgente.exemploPercentualTotal') }}</i>
                                    </small>
                                    <validation-provider
                                      #default="{ errors }"
                                      :rules="{ regex: /^[+]?\d+(\.\d+)?$/, min_value: currentConfig.tipoValorUSDC==='A' ? 6:0.1, max_value:currentConfig.tipoValorUSDC==='A'? 99999 : 99 }"
                                      :name="currentConfig.tipoValorUSDC==='A'? $t('spot.meuAgente.valorTotalDisponibilizar'): $t('spot.meuAgente.percentualTotalDisponibilizar')"
                                    >
                                      <b-form-input
                                        id="precoMaxTotalUSDC"
                                        v-model="currentConfig.precoMaxTotalUSDC"
                                        :state="errors.length > 0 ? false: null"
                                      />
                                      <small class="text-danger">{{ errors[0] }}</small>
                                    </validation-provider>
                                  </b-form-group>
                                </b-col>
                                <b-col lg="12">
                                  <b-row>
                                    <b-col lg="4">
                                      <b-form-group
                                        :label="$t('spot.meuAgente.saida')"
                                        label-for="alvo-usdc"
                                      >
                                        <b-form-select
                                          v-model="currentConfig.alvoVendaUSDC"
                                          :options="alvosOptionsUSDC(currentConfig)"
                                          input-id="alvo-usdc"
                                          @change="alteraAlvo('USDC',currentConfig)"
                                        />
                                      </b-form-group>
                                    </b-col>
                                  </b-row>
                                  <b-row v-if="currentConfig.alvoVendaUSDC=='0'">
                                    <b-col lg="2">
                                      <b-form-group
                                        :label="$t('spot.meuAgente.primeiraSaida')"
                                        label-for="alvo1-usdc"
                                      >
                                        <validation-provider
                                          #default="{ errors }"
                                          :rules="{ required: currentConfig.paridadeUSDC == 'true' && currentConfig.alvoVendaUSDC == '0' }"
                                          name="Primeira saída (%)"
                                        >
                                          <b-form-select
                                            v-model="currentConfig.alvoVendaParcial1USDC"
                                            :options="alvosPorcentagemOptions"
                                            input-id="alvo1-usdc"
                                            :state="errors.length > 0 ? false: null"
                                          />
                                          <small class="text-danger">{{ errors[0] }}</small>
                                        </validation-provider>
                                      </b-form-group>
                                    </b-col>
                                    <b-col lg="2">
                                      <b-form-group
                                        :label="$t('spot.meuAgente.SegundaSaida')"
                                        label-for="alvo2-usdc"
                                      >
                                        <b-form-input
                                          id="alvo2-usdc"
                                          disabled
                                          :value="100-currentConfig.alvoVendaParcial1USDC"
                                        />
                                      </b-form-group>
                                    </b-col>
                                  </b-row>
                                  <b-row v-if="currentConfig.alvoVendaUSDC=='0'">
                                    <b-col lg="6">
                                      <b-alert
                                        show
                                        variant="warning"
                                      >
                                        <div class="alert-body">
                                          <span class="ml-25">{{ $t('spot.meuAgente.avisoAlvoInferior') }}</span>
                                        </div>
                                      </b-alert>
                                    </b-col>
                                  </b-row>
                                </b-col>
                              </b-row>
                            </app-collapse-item>
                          </app-collapse>
                        </b-tab>

                        <b-tab
                          v-if="isHabilitaParidade('BTC')"
                          title="Bitcoin"
                          title-item-class="currency-tab-item"
                          title-link-class="currency-tab-link"
                        >
                          <b-row class="mb-2">
                            <b-col cols="12">
                              <h5 class="mb-2 text-muted">
                                {{ $t('spot.meuAgente.descricaoBitcoin') }}
                              </h5>
                              <h5 class="font-medium">
                                {{ $t('spot.meuAgente.ativarInvestimentosBitcoin') }}
                              </h5>
                              <div class="demo-inline-spacing">
                                <b-form-radio-group v-model="currentConfig.paridadeBTC">
                                  <b-form-radio
                                    name="ativar-btc"
                                    value="true"
                                    class="custom-control-success"
                                  >
                                    {{ $t('spot.meuAgente.sim') }}
                                  </b-form-radio>
                                  <b-form-radio
                                    name="ativar-btc"
                                    value="false"
                                    class="custom-control-danger"
                                  >
                                    {{ $t('spot.meuAgente.nao') }}
                                  </b-form-radio>
                                </b-form-radio-group>
                              </div>
                            </b-col>
                          </b-row>
                          <b-row class="mb-2">
                            <b-col cols="12">
                              <h5 class="font-medium">
                                {{ $t('spot.meuAgente.quantoInvestir') }}
                              </h5>
                              <div class="demo-inline-spacing">
                                <b-form-radio-group v-model="currentConfig.tipoValorBTC">
                                  <b-form-radio
                                    name="tipo-valor-btc"
                                    value="A"
                                    @change="trocaTipoValorBTC"
                                  >
                                    {{ $t('spot.meuAgente.emValor') }}
                                  </b-form-radio>
                                  <b-form-radio
                                    name="tipo-valor-btc"
                                    value="P"
                                    @change="trocaTipoValorBTC"
                                  >
                                    {{ $t('spot.meuAgente.emPorcentagem') }}
                                  </b-form-radio>
                                </b-form-radio-group>
                              </div>
                            </b-col>
                          </b-row>
                          <b-row>
                            <b-col lg="4">
                              <b-form-group
                                label-for="precoMaxOrdemBTC"
                              >
                                <label v-if="currentConfig.tipoValorBTC=='A'">{{ $t('spot.meuAgente.valorPorOperacao') }}</label>
                                <label v-else>{{ $t('spot.meuAgente.percentualPorOperacao') }}</label>
                                <small
                                  v-if="currentConfig.tipoValorBTC=='A'"
                                  class="text-muted"
                                >
                                  <i>  {{ $t('spot.meuAgente.exemploValorPorOperacao') }}</i>
                                </small>
                                <small
                                  v-else
                                  class="text-muted"
                                >
                                  <i>  {{ $t('spot.meuAgente.exemploPercentualPorOperacao') }}</i>
                                </small>
                                <validation-provider
                                  #default="{ errors }"
                                  :rules="{ required: currentConfig.paridadeBTC === 'true', regex: /^[+]?\d+(\.\d+)?$/, min_value: currentConfig.tipoValorBTC==='A' ? 0.0004:0.1, max_value:currentConfig.tipoValorBTC==='A'? 1 : 99 }"
                                  :name="currentConfig.tipoValorBTC === 'A'? $t('spot.meuAgente.valorPorOperacao'): $t('spot.meuAgente.percentualPorOperacao')"
                                >
                                  <b-form-input
                                    id="precoMaxOrdemBTC"
                                    v-model="currentConfig.precoMaxOrdemBTC"
                                    :state="errors.length > 0 ? false: null"
                                  />
                                  <small class="form-text text-muted">{{ currentConfig.tipoValorBTC=='A'?$t('spot.meuAgente.aproximadamente') + (currentConfig.precoMaxOrdemBTC * cotacaoBTCUSDT).toFixed(2)+' USD':'' }}</small>
                                  <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                              </b-form-group>
                            </b-col>
                          </b-row>
                          <app-collapse
                            accordion
                            type="border"
                          >
                            <app-collapse-item :title="$t('spot.meuAgente.configuracoesAvancadasBTC')">
                              <b-row>
                                <b-col lg="4">
                                  <b-form-group :description="$t('spot.meuAgente.deixeEmBrancoParaOperar')">
                                    <label v-if="currentConfig.tipoValorBTC=='A'">{{ $t('spot.meuAgente.valorTotalDisponibilizar') }}</label>
                                    <label v-else> {{ $t('spot.meuAgente.percentualTotalDisponibilizar') }}</label>
                                    <small
                                      v-if="currentConfig.tipoValorBTC=='A'"
                                      class="text-muted"
                                    >
                                      <i>  {{ $t('spot.meuAgente.exemploValorTotalDisponibilizar') }}</i>
                                    </small>
                                    <small
                                      v-else
                                      class="text-muted"
                                    >
                                      <i>  {{ $t('spot.meuAgente.exemploPercentualTotal') }}</i>
                                    </small>
                                    <validation-provider
                                      #default="{ errors }"
                                      :rules="{ regex: /^[+]?\d+(\.\d+)?$/, min_value: currentConfig.tipoValorBTC==='A' ? 0.0004:0.1, max_value:currentConfig.tipoValorBTC==='A'? 2 : 99 }"
                                      :name="currentConfig.tipoValorBTC==='A'? $t('spot.meuAgente.valorTotalDisponibilizar'): $t('spot.meuAgente.percentualTotalDisponibilizar')"
                                    >
                                      <b-form-input
                                        id="precoMaxTotalBTC"
                                        v-model="currentConfig.precoMaxTotalBTC"
                                        :state="errors.length > 0 ? false: null"
                                      />
                                      <small class="form-text text-muted">{{ currentConfig.tipoValorBTC=='A'?$t('spot.meuAgente.aproximadamente')+ (currentConfig.precoMaxTotalBTC * cotacaoBTCUSDT).toFixed(2)+' USD':'' }}</small>
                                      <small class="text-danger">{{ errors[0] }}</small>
                                    </validation-provider>
                                  </b-form-group>
                                </b-col>
                                <b-col lg="12">
                                  <b-row>
                                    <b-col lg="4">
                                      <b-form-group
                                        :label="$t('spot.meuAgente.saida')"
                                        label-for="alvo-btc"
                                      >
                                        <b-form-select
                                          v-model="currentConfig.alvoVendaBTC"
                                          :options="alvosOptionsBTC(currentConfig)"
                                          input-id="alvo-btc"
                                          @change="alteraAlvo('BTC',currentConfig)"
                                        />
                                      </b-form-group>
                                    </b-col>
                                  </b-row>
                                  <b-row v-if="currentConfig.alvoVendaBTC=='0'">
                                    <b-col lg="2">
                                      <b-form-group
                                        :label="$t('spot.meuAgente.primeiraSaida')"
                                        label-for="alvo1-btc"
                                      >
                                        <validation-provider
                                          #default="{ errors }"
                                          :rules="{ required: currentConfig.paridadeBTC=='true' && currentConfig.alvoVendaBTC=='0' }"
                                          name="Primeira saída (%)"
                                        >
                                          <b-form-select
                                            v-model="currentConfig.alvoVendaParcial1BTC"
                                            :options="alvosPorcentagemOptions"
                                            input-id="alvo1-btc"
                                            :state="errors.length > 0 ? false: null"
                                          />
                                          <small class="text-danger">{{ errors[0] }}</small>
                                        </validation-provider>
                                      </b-form-group>
                                    </b-col>
                                    <b-col lg="2">
                                      <b-form-group
                                        :label="$t('spot.meuAgente.segundaSaida')"
                                        label-for="alvo2-btc"
                                      >
                                        <b-form-input
                                          id="alvo2-btc"
                                          disabled
                                          :value="100-currentConfig.alvoVendaParcial1BTC"
                                        />
                                      </b-form-group>
                                    </b-col>
                                  </b-row>
                                  <b-row v-if="currentConfig.alvoVendaBTC=='0'">
                                    <b-col lg="6">
                                      <b-alert
                                        show
                                        variant="warning"
                                      >
                                        <div class="alert-body">
                                          <span class="ml-25">{{ $t('spot.meuAgente.alertaBTC') }}</span>
                                        </div>
                                      </b-alert>
                                    </b-col>
                                  </b-row>
                                </b-col>
                              </b-row>
                            </app-collapse-item>
                          </app-collapse>
                        </b-tab>

                        <b-tab
                          v-if="isHabilitaParidade('BRL')"
                          title="BRL"
                        >
                          <b-row class="mb-2">
                            <b-col cols="12">
                              <h5 class="mb-2 text-muted">
                                {{ $t('spot.meuAgente.descricaoBRL') }}
                              </h5>
                              <h5 class="font-medium">
                                {{ $t('spot.meuAgente.ativarInvestimentosBRL') }}
                              </h5>
                              <div class="demo-inline-spacing">
                                <b-form-radio-group v-model="currentConfig.paridadeBRL">
                                  <b-form-radio
                                    name="ativar-brl"
                                    value="true"
                                    class="custom-control-success"
                                  >
                                    {{ $t('spot.meuAgente.sim') }}
                                  </b-form-radio>
                                  <b-form-radio
                                    name="ativar-brl"
                                    value="false"
                                    class="custom-control-danger"
                                  >
                                    {{ $t('spot.meuAgente.nao') }}
                                  </b-form-radio>
                                </b-form-radio-group>
                              </div>
                            </b-col>
                          </b-row>
                          <b-row class="mb-2">
                            <b-col cols="12">
                              <h5 class="font-medium">
                                {{ $t('spot.meuAgente.quantoInvestir') }}

                              </h5>
                              <div class="demo-inline-spacing">
                                <b-form-radio-group v-model="currentConfig.tipoValorBRL">
                                  <b-form-radio
                                    name="tipo-valor-brl"
                                    value="A"
                                    @change="trocaTipoValorBRL"
                                  >
                                    {{ $t('spot.meuAgente.emValor') }}
                                  </b-form-radio>
                                  <b-form-radio
                                    name="tipo-valor-brl"
                                    value="P"
                                    @change="trocaTipoValorBRL"
                                  >
                                    {{ $t('spot.meuAgente.emPorcentagem') }}
                                  </b-form-radio>
                                </b-form-radio-group>
                              </div>
                            </b-col>
                          </b-row>
                          <b-row>
                            <b-col lg="4">
                              <b-form-group
                                label-for="precoMaxOrdemBRL"
                              >
                                <label v-if="currentConfig.tipoValorBRL=='A'">{{ $t('spot.meuAgente.valorPorOperacao') }}</label>
                                <label v-else>{{ $t('spot.meuAgente.percentualPorOperacao') }}</label>
                                <small
                                  v-if="currentConfig.tipoValorBRL=='A'"
                                  class="text-muted"
                                >
                                  <i>  {{ $t('spot.meuAgente.exemploValor') }}</i>
                                </small>
                                <small
                                  v-else
                                  class="text-muted"
                                >
                                  <i>  {{ $t('spot.meuAgente.exemploPercentual') }}</i>
                                </small>
                                <validation-provider
                                  #default="{ errors }"
                                  :rules="{ required: currentConfig.paridadeBRL === 'true', regex: /^[+]?\d+(\.\d+)?$/, min_value: currentConfig.tipoValorBRL==='A' ? 6:0.1, max_value:currentConfig.tipoValorBRL==='A'? 99999 : 99 }"
                                  :name="currentConfig.tipoValorBRL === 'A'? $t('spot.meuAgente.valorPorOperacao'): $t('spot.meuAgente.percentualPorOperacao')"
                                  :custom-messages="{
                                    min_value: currentConfig.tipoValorUSD === 'A'
                                      ? 'O valor deve ser maior ou igual a 6.'
                                      : 'O valor deve ser maior ou igual a 0.1.',
                                  }"
                                >
                                  <b-form-input
                                    id="precoMaxOrdemBRL"
                                    v-model="currentConfig.precoMaxOrdemBRL"
                                    :state="errors.length > 0 ? false: null"
                                  />
                                  <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                              </b-form-group>
                            </b-col>
                          </b-row>
                          <app-collapse
                            accordion
                            type="border"
                          >
                            <app-collapse-item :title="$t('spot.meuAgente.configuracoesAvancadasBRL')">
                              <b-row>
                                <b-col lg="4">
                                  <b-form-group :description="$t('spot.meuAgente.deixeEmBrancoParaOperar')">
                                    <label v-if="currentConfig.tipoValorBRL=='A'">{{ $t('spot.meuAgente.valorTotalDisponibilizar') }}</label>
                                    <label v-else> {{ $t('spot.meuAgente.percentualTotalDisponibilizar') }}</label>
                                    <small
                                      v-if="currentConfig.tipoValorBRL=='A'"
                                      class="text-muted"
                                    >
                                      <i> {{ $t('spot.meuAgente.exemploValorTotal') }}</i>
                                    </small>
                                    <small
                                      v-else
                                      class="text-muted"
                                    >
                                      <i>  {{ $t('spot.meuAgente.exemploPercentualTotal') }}</i>
                                    </small>
                                    <validation-provider
                                      #default="{ errors }"
                                      :rules="{ regex: /^[+]?\d+(\.\d+)?$/, min_value: currentConfig.tipoValorBRL==='A' ? 6:0.1, max_value:currentConfig.tipoValorBRL==='A'? 99999 : 99 }"
                                      :name="currentConfig.tipoValorBRL==='A'? $t('spot.meuAgente.valorTotalDisponibilizar'): $t('spot.meuAgente.percentualTotalDisponibilizar')"
                                      :custom-messages="{
                                        min_value: currentConfig.tipoValorUSD === 'A'
                                          ? 'O valor deve ser maior ou igual a 6.'
                                          : 'O valor deve ser maior ou igual a 0.1.',
                                      }"
                                    >
                                      <b-form-input
                                        id="precoMaxTotalBRL"
                                        v-model="currentConfig.precoMaxTotalBRL"
                                        :state="errors.length > 0 ? false: null"
                                      />
                                      <small class="text-danger">{{ errors[0] }}</small>
                                    </validation-provider>
                                  </b-form-group>
                                </b-col>
                              </b-row>
                              <b-row>
                                <b-col lg="12">
                                  <b-row>
                                    <b-col lg="4">
                                      <b-form-group
                                        :label="$t('spot.meuAgente.saida')"
                                        label-for="alvo-brl"
                                      >
                                        <b-form-select
                                          v-model="currentConfig.alvoVendaBRL"
                                          :options="alvosOptionsBRL(currentConfig)"
                                          input-id="alvo-brl"
                                          @change="alteraAlvo('BRL',currentConfig)"
                                        />
                                      </b-form-group>
                                    </b-col>
                                  </b-row>
                                  <b-row v-if="currentConfig.alvoVendaBRL=='0'">
                                    <b-col lg="2">
                                      <b-form-group
                                        :label="$t('spot.meuAgente.primeiraSaida')"
                                        label-for="alvo1-brl"
                                      >
                                        <validation-provider
                                          #default="{ errors }"
                                          :rules="{ required: currentConfig.paridadeBRL == 'true' && currentConfig.alvoVendaBRL == '0' }"
                                          name="Primeira saída (%)"
                                        >
                                          <b-form-select
                                            v-model="currentConfig.alvoVendaParcial1BRL"
                                            :options="alvosPorcentagemOptions"
                                            input-id="alvo1-brl"
                                            :state="errors.length > 0 ? false: null"
                                          />
                                          <small class="text-danger">{{ errors[0] }}</small>
                                        </validation-provider>
                                      </b-form-group>
                                    </b-col>
                                    <b-col lg="2">
                                      <b-form-group
                                        :label="$t('spot.meuAgente.segundaSaida')"
                                        label-for="alvo2-brl"
                                      >
                                        <b-form-input
                                          id="alvo2-brl"
                                          disabled
                                          :value="100-currentConfig.alvoVendaParcial1BRL"
                                        />
                                      </b-form-group>
                                    </b-col>
                                  </b-row>
                                  <b-row v-if="currentConfig.alvoVendaBRL=='0'">
                                    <b-col lg="6">
                                      <b-alert
                                        show
                                        variant="warning"
                                      >
                                        <div class="alert-body">
                                          <span class="ml-25">{{ $t('spot.meuAgente.avisoAlvoInferior') }}</span>
                                        </div>
                                      </b-alert>
                                    </b-col>
                                  </b-row>
                                </b-col>
                              </b-row>
                            </app-collapse-item>
                          </app-collapse>
                        </b-tab></b-tabs></div>
                  </div>

                  <!-- Botão Salvar -->
                  <div class="config-actions">
                    <b-button
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      variant="primary"
                      size="lg"
                      class="save-button"
                      @click.prevent="confirmarSalvar"
                    >
                      <feather-icon
                        icon="SaveIcon"
                        class="mr-50"
                      />
                      {{ $t('spot.meuAgente.salvarBtn') }}
                    </b-button>
                  </div>
                </div>
              </b-form>
            </validation-observer>
          </b-card-body>
        </b-card>

      </template>

    </b-overlay>
  </div>
</template>

<script>
/* eslint-disable */
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, minValue, maxValue } from '@validations'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@/@core/components/app-collapse/AppCollapseItem.vue'
import {
 BRow, BCard, BCol, BAvatar, BBadge, BButton, BCardHeader, BCardFooter,  BCardTitle, BCardText, VBTooltip, BProgress, BOverlay, BTab, BTabs, BFormRadioGroup, BFormRadio, BFormInput, BFormTextarea, BFormGroup, BFormSelect, BForm, BAlert, BCardBody, BMedia, BMediaAside, BMediaBody, BSpinner,
} from 'bootstrap-vue'
import { subscribeSymbol } from '@/services/binance/spot/binance'
import Ripple from 'vue-ripple-directive'
import utilsMixin from '@/mixins/utilsMixin'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {

  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },

  mixins: [utilsMixin],

  components: {
    ValidationProvider,
    ValidationObserver,
    BRow,
    BCard,
    BCol,
    BAvatar,
    BButton,
    BCardText,
    VBTooltip,
    BProgress,
    BOverlay,
    BTab,
    BTabs,
    BFormRadio,
    BFormRadioGroup,
    AppCollapse,
    AppCollapseItem,
    BFormInput,
    BFormTextarea,
    BFormGroup,
    BFormSelect,
    BForm,
    BAlert,
    BCardBody,
    BMedia,
    BMediaAside,
    BMediaBody,
    BCardHeader,
    BCardFooter,
    BCardTitle,
    BBadge,
    BSpinner,
  },

  data() {
    return {
      loadingOverlay: false,
      user_data: {
        resumoSaldos: []
      },
      required,
      minValue,
      maxValue,
      errorMessage: null,

      // Seletor de corretora
      selectedExchange: null,
      exchangeConfig: {
        binance: {
          name: 'Binance',
          variant: 'warning',
          cardClass: 'binance-theme',
          color: '#F0B90B',
        },
        mexc: {
          name: 'MEXC',
          variant: 'info',
          cardClass: 'mexc-theme',
          color: '#005CE6',
        },
      },

      alvosPorcentagemOptions: [
        {text: "30", value: 30},
        {text: "40", value: 40},
        {text: "50", value: 50},
        {text: "60", value: 60},
        {text: "70", value: 70},
      ],

    }
  },

  computed: {
    // Corretoras disponíveis baseadas nos dados reais
    availableExchanges() {
      if (!this.user_data.configs) return {}
      
      const exchanges = {}
      this.user_data.configs.forEach(config => {
        const key = config.corretora.toLowerCase()
        if (this.exchangeConfig[key]) {
          exchanges[key] = this.exchangeConfig[key]
        }
      })
      
      return exchanges
    },

    // Configuração única filtrada pela corretora selecionada
    currentConfig(){
      if (!this.user_data.configs || !this.selectedExchange) return null
      
      const configs = this.user_data.configs.filter(config => 
        config.corretora.toLowerCase() === this.selectedExchange
      )
      
      // Retorna a primeira configuração encontrada (assumindo uma por corretora)
      return configs.length > 0 ? configs[0] : null
    },

    // Resumo de saldos filtrado pela corretora selecionada
    filteredResumoSaldos() {
      if (!this.user_data.resumoSaldos || !this.selectedExchange) return []
      
      const configsCorretora = this.currentConfig ? [this.currentConfig.perfil] : []
      
      return this.user_data.resumoSaldos
        .filter(resumo => resumo.corretora.toLowerCase() === this.selectedExchange)
        .map(resumo => ({
          ...resumo,
          perfis: resumo.perfis.filter(perfil => 
            configsCorretora.includes(perfil.perfil)
          )
        }))
    },

    stopVendaLimit(){
      return [
        {value: null, text: this.$i18n.t('spot.meuAgente.seguirAgente')},
        {value: "2", text: '2%'},
        {value: "3", text: '3%'},
        {value: "4", text: '4%'},
        {value: "5", text: '5%'},
        {value: "6", text: '6%'},
        {value: "7", text: '7%'},
        {value: "8", text: '8%'},
        {value: "9", text: '9%'},
        {value: "10", text: '10%'},
      ]
    },
    alvosOptions() {
      return [
        {value: "1", text: this.$i18n.t('spot.meuAgente.primeiroAlvo')},
        {value: "2", text: this.$i18n.t('spot.meuAgente.segundoAlvo')}
      ]
    },
    alvosOptionsPersonalizado() {
      return [
        {value: "1", text: this.$i18n.t('spot.meuAgente.primeiroAlvo')},
        {value: "2", text: this.$i18n.t('spot.meuAgente.segundoAlvo')},
        {value: "0", text: this.$i18n.t('spot.meuAgente.personalizado')}
      ]
    },

    cotacaoBTCUSDT() {
      if(this.$store.state.binanceSpot.tickers && this.$store.state.binanceSpot.tickers['BTCUSDT']){
        return this.$store.state.binanceSpot.tickers['BTCUSDT'].price
      }
    },
  },

  methods: {
    // Seletor de corretora
    selectExchange(exchangeKey) {
      this.selectedExchange = exchangeKey
    },

    // Inicializar a primeira corretora disponível baseada nos configs
    initializeExchange() {
      if (this.user_data.configs && this.user_data.configs.length > 0 && !this.selectedExchange) {
        const firstExchange = this.user_data.configs[0].corretora.toLowerCase()
        if (this.exchangeConfig[firstExchange]) {
          this.selectedExchange = firstExchange
        }
      }
    },


    // Lighten a HEX color by percentage (mix with white)
    // Método para padronizar o variant do badge de exchange
    getExchangeBadgeVariant(exchange) {
      if (!exchange) return 'secondary'
      
      const exchangeKey = exchange.toLowerCase()
      const config = this.exchangeConfig[exchangeKey]
      
      if (config && config.variant) {
        return config.variant
      }
      
      // Fallback baseado no nome da exchange
      if (exchangeKey === 'binance') return 'warning'
      if (exchangeKey === 'mexc') return 'info'
      
      return 'secondary'
    },

    lightenColor(hexColor, percent) {
      try {
        if (!hexColor) return '#ffffff'
        let hex = hexColor.toString().replace('#', '')
        if (hex.length === 3) {
          hex = hex.split('').map(ch => ch + ch).join('')
        }
        const r = parseInt(hex.substring(0, 2), 16)
        const g = parseInt(hex.substring(2, 4), 16)
        const b = parseInt(hex.substring(4, 6), 16)
        const p = Math.max(0, Math.min(100, Number(percent))) / 100
        const rn = Math.round(r + (255 - r) * p)
        const gn = Math.round(g + (255 - g) * p)
        const bn = Math.round(b + (255 - b) * p)
        const toHex = v => v.toString(16).padStart(2, '0')
        return `#${toHex(rn)}${toHex(gn)}${toHex(bn)}`
      } catch (e) {
        return hexColor
      }
    },

    getIcon(simbolo) {
      return `${process.env.VUE_APP_STATIC}icons/${simbolo.toLowerCase()}.png`
    },

    // Retorna a classe CSS para cada paridade
    getParidadeCardClass(paridade) {
      const par = paridade.toUpperCase()
      return `paridade-card-${par.toLowerCase()}`
    },

    isHabilitaParidade(paridade){
      if(this.user_data.resumoSaldos){
        return this.user_data.resumoSaldos.some(saldo => saldo.paridade === paridade)
      }
    },

    tipoOrdemVendaOptions(config){

      const tipoOrdemVendaOptionsInt = [
        {value: "OCO", text: 'OCO (Com stop)'},
        {value: "Limite", text: 'Limite (Sem stop)'}
      ]

      const textAgente = tipoOrdemVendaOptionsInt.find(v=> v.value==this.currentConfig.tipoOrdemVendaAgente).text
      tipoOrdemVendaOptionsInt.unshift({value: null, text: `Seguir agente (${textAgente})`})

      return tipoOrdemVendaOptionsInt;
    },

    stopOptions(config){

      const stopOptionsInt = [
        {value: "0.5", text: 'Se subir 0.50%'},
        {value: "0.65", text: 'Se subir 0.65%'},
        {value: "0.80", text: 'Se subir 0.80%'},
        {value: "1", text: 'Se subir 1%'},
        {value: "0", text: 'Não cancelar'}
      ]

      const textAgente = stopOptionsInt.find(v=> v.value==this.currentConfig.stopCompraPorcentagemAgente).text
      stopOptionsInt.unshift({value: null, text: `Seguir agente (${textAgente})`})

      return stopOptionsInt;
    },

    isTipoVendaLimite(config){

      if(this.currentConfig.tipoOrdemVenda==null){
        if(this.currentConfig.tipoOrdemVendaAgente=='Limite'){
          return true;
        }
      }else if(this.currentConfig.tipoOrdemVenda=='Limite'){
          return true;
      }
        return false;
    },

    alteraTipoVenda(config){
      if(this.currentConfig.tipoOrdemVenda=="OCO"){
        if(this.currentConfig.alvoVendaBTC=="0"){
          this.currentConfig.alvoVendaBTC =1
        }
        if(this.currentConfig.alvoVendaUSDT=="0"){
          this.currentConfig.alvoVendaUSDT =1
        }
        if(this.currentConfig.alvoVendaBRL=="0"){
          this.currentConfig.alvoVendaBRL =1
        }
        if(this.currentConfig.alvoVendaUSDC=="0"){
          this.currentConfig.alvoVendaUSDC =1
        }
      }
    },

    alvosOptionsBTC: function(config){
      var options = [];
        options.push({text: "Alvo 1 (Recomendado)", value:"1"})
        options.push({text: "Alvo 2", value:"2"})
        // if(this.isTipoVendaLimite(config)){
         //  options.push({text: "Personalizado", value:"0"})
        // }
      return options;
    },

    alvosOptionsUSD: function(config){
      var options = [];
        options.push({text: "Alvo 1 (Recomendado)", value:"1"})
        options.push({text: "Alvo 2", value:"2"})
        // if(this.isTipoVendaLimite(config)){
          // options.push({text: "Personalizado", value:"0"})
        // }
      return options;
    },

    alvosOptionsBRL: function(config){
      var options = [];
        options.push({text: "Alvo 1 (Recomendado)", value:"1"})
        options.push({text: "Alvo 2", value:"2"})
        // if(this.isTipoVendaLimite(config)){
          // options.push({text: "Personalizado", value:"0"})
        // }
      return options;
    },

    alvosOptionsUSDC: function(config){
      var options = [];
        options.push({text: "Alvo 1 (Recomendado)", value:"1"})
        options.push({text: "Alvo 2", value:"2"})
        // if(this.isTipoVendaLimite(config)){
          // options.push({text: "Personalizado", value:"0"})
        // }
      return options;
    },

    alteraAlvo(paridade, config){
      if(paridade=='USDT'){
        if(this.currentConfig.alvoVendaUSD=='0'){
          this.currentConfig.alvoVendaParcial1USD = 60
        }
      }else if(paridade=='BTC'){
        if(this.currentConfig.alvoVendaBTC=='0'){
          this.currentConfig.alvoVendaParcial1BTC = 60
        }
      }else if(paridade=='BRL'){
        if(this.currentConfig.alvoVendaBRL=='0'){
          this.currentConfig.alvoVendaParcial1BRL = 60
        }
      }else if(paridade=='USDC'){
        if(this.currentConfig.alvoVendaUSDC=='0'){
          this.currentConfig.alvoVendaParcial1USDC = 60
        }
      }
    },

    confirmarSalvar(id) {
      // validações que não estao sendo pegas pelo validator quando nao esta na aba ativa
      let mensagens = this.validacaoExtra(this.user_data)
      if (mensagens) {
          this.$swal({
            title: 'Error!',
            html: mensagens,
            icon: 'error',
            confirmButtonText: 'Ok, vou corrigir!',
            customClass: {
              confirmButton: 'btn btn-primary',
            },
            buttonsStyling: false,
          })
      } else {
        this.$swal({
          title: 'Confirmar as alterações',
          text: 'Revise os valores. Ao clicar no botão verde, você vai começar a investir os valores definidos. Também poderá pausar ou alterar a qualquer momento.',
          icon: 'warning',
          confirmButtonText: 'Ok, está tudo certo!',
          showCancelButton: true,
          cancelButtonText: 'Abortar',
          customClass: {
            confirmButton: 'btn btn-success',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        }).then(result => {
          if (result.value) {
            this.submitForm()
          }
        })
      }
    },

    trocaTipoValorBTC(){
      if (this.currentConfig) {
        this.currentConfig.precoMaxTotalBTC = null;
        this.currentConfig.precoMaxOrdemBTC = null;
      }
    },

    trocaTipoValorUSD(){
      if (this.currentConfig) {
        this.currentConfig.precoMaxTotalUSD = null;
        this.currentConfig.precoMaxOrdemUSD = null;
      }
    },

    trocaTipoValorBRL(){
      if (this.currentConfig) {
        this.currentConfig.precoMaxTotalBRL = null;
        this.currentConfig.precoMaxOrdemBRL = null;
      }
    },

    trocaTipoValorUSDC(){
      if (this.currentConfig) {
        this.currentConfig.precoMaxTotalUSDC = null;
        this.currentConfig.precoMaxOrdemUSDC = null;
      }
    },

    validacaoExtra(data){
      let mensagemValidacao = null
      data.configs.map(function(value, key) {
        if (!!value.precoMaxTotalBTC  && !/^[+]?\d+(\.\d+)?$/.test(value.precoMaxTotalBTC)) {
          mensagemValidacao = "O campo 'Valor total a disponibilizar (BTC)' possui um formato inválido. <br>"
        }
        if (!/^[+]?\d+(\.\d+)?$/.test(value.precoMaxOrdemBTC)) {
          mensagemValidacao += "O campo 'Valor por operação (BTC)' possui um formato inválido. <br>"
        }

        if (!!value.precoMaxTotalUSD && !/^[+]?\d+(\.\d+)?$/.test(value.precoMaxTotalUSD)) {
          mensagemValidacao += "O campo 'Valor total a disponibilizar (USD)' possui um formato inválido. <br>"
        }

        if (!/^[+]?\d+(\.\d+)?$/.test(value.precoMaxOrdemUSD)) {
          mensagemValidacao += "O campo 'Valor por operação (USD)' possui um formato inválido. <br>"
        }

        if (!!value.precoMaxTotalUSDC && !/^[+]?\d+(\.\d+)?$/.test(value.precoMaxTotalUSDC)) {
          mensagemValidacao += "O campo 'Valor total a disponibilizar (USDC)' possui um formato inválido. <br>"
        }

        if (!/^[+]?\d+(\.\d+)?$/.test(value.precoMaxOrdemUSDC)) {
          mensagemValidacao += "O campo 'Valor por operação (USDC)' possui um formato inválido. <br>"
        }

        if (!!value.precoMaxTotalBRL && !/^[+]?\d+(\.\d+)?$/.test(value.precoMaxTotalBRL)) {
          mensagemValidacao += "O campo 'Valor total a disponibilizar (BRL)' possui um formato inválido. <br>"
        }

        if (!/^[+]?\d+(\.\d+)?$/.test(value.precoMaxOrdemBRL)) {
          mensagemValidacao += "O campo 'Valor por operação (BRL)' possui um formato inválido. <br>"
        }
      })
      return mensagemValidacao
    },

    submitForm() {
      this.$refs.simpleRules.validate().then(success => {
        if (success) {
          this.loadingOverlay = true
          // Enviar apenas a configuração atual
          const configToSave = this.currentConfig ? [this.currentConfig] : []
          this.$store.dispatch('meu-agente-spot/setMeuAgentePerfilConfig', configToSave)
            .then(() => {
              // Recarregar dados para atualizar as configurações e o resumo de saldos
              this.getMeuAgente(this.$route.params.id)
              this.$swal({
                title: 'Sucesso!',
                text: 'Agente configurado com sucesso',
                icon: 'success',
                customClass: {
                  confirmButton: 'btn btn-primary',
                },
                buttonsStyling: false,
              })
            })
            .catch(error => {
              this.$swal({
                title: 'Error!',
                text: error.response.data[0].erro,
                icon: 'error',
                customClass: {
                  confirmButton: 'btn btn-primary',
                },
                buttonsStyling: false,
              })
          }).finally(() => {
            this.loadingOverlay = false
          });
        }
      })
    },

    getMeuAgente(id) {
      this.loadingOverlay = true
      this.$store.dispatch('meu-agente-spot/getMeuAgenteOperacao', id)
        .then(res => {
          this.user_data = res.data
          this.errorMessage = null
          // Inicializar corretora após carregar dados
          this.$nextTick(() => {
            this.initializeExchange()
          })
        }).catch(error => {
          this.errorMessage = "API inválida. <a href='/tutorial/primeiros-passos/como-criar-api-binance'>Clique aqui</a> para obter mais informações"
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        }).finally(() => {
          this.loadingOverlay = false
        });
    },

  },

  created() {
    this.getMeuAgente(this.$route.params.id)
      subscribeSymbol("BTCUSDT")
  }

}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-sweetalert.scss';
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

// Soulf redesign — retint local dos tokens de dark-mode do Vuexy (só CSS,
// sem alterar template/lógica/dados): cards navy (#283046) -> quase-preto Soulf.
// Variáveis SCSS reatribuídas apenas neste bloco <style scoped>, sem efeito
// em outros componentes.
$theme-dark-card-bg: #0F0F11;
$theme-dark-border-color: rgba(255, 255, 255, 0.10);
$theme-dark-body-bg: #000000;
$theme-dark-headings-color: #ffffff;
$theme-dark-body-color: #c9ccd1;

.meu-agente-operacao {

  // Dashboard de Saldos Moderno
  .saldo-dashboard-card {
    box-shadow: 0 4px 25px rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    border: none;
    overflow: hidden;

    .dashboard-header {
      background: $white;
      border-bottom: 1px solid rgba($border-color, 0.5);
      border-radius: 12px 12px 0 0;
      padding: 1.5rem;

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
        gap: 1.5rem;

        .header-title {
          display: flex;
          align-items: center;
          gap: 1rem;

          .title-text {
            font-size: 1.25rem;
            font-weight: 600;
            color: $headings-color;
            margin-bottom: 0.25rem;
          }

          .title-subtitle {
            font-size: 0.875rem;
        color: $text-muted;
            font-weight: 500;
          }
        }


        @media (max-width: 768px) {
          flex-direction: column;
          align-items: flex-start;
          gap: 1rem;
        }
      }
    }

    .saldo-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
      gap: 1.5rem;
      padding: 2rem;

      @media (max-width: 768px) {
        grid-template-columns: 1fr;
        padding: 1.5rem;
        gap: 1.25rem;
      }
    }

    .saldo-card-modern {
        background: $white;
      border: 1px solid rgba($border-color, 0.3);
      border-radius: 12px;
      overflow: hidden;
        transition: all 0.3s ease;
        position: relative;

        &:hover {
        transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        border-color: $primary;
      }

      // Classes específicas por paridade
      &.paridade-card-usdt {
        &:hover { box-shadow: 0 8px 25px rgba(38, 166, 154, 0.1); }
      }

      &.paridade-card-usdc {
        &:hover { box-shadow: 0 8px 25px rgba(33, 150, 243, 0.1); }
      }

      &.paridade-card-btc {
        &:hover { box-shadow: 0 8px 25px rgba(255, 152, 0, 0.1); }
      }

      &.paridade-card-brl {
        &:hover { box-shadow: 0 8px 25px rgba(76, 175, 80, 0.1); }
      }

      .card-header-modern {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1.5rem;
        background: rgba($body-color, 0.02);
        border-bottom: 1px solid rgba($border-color, 0.08);

        .currency-info {
          display: flex;
          align-items: center;
          gap: 0.875rem;

          .currency-icon {
            .currency-avatar {
              border: 2px solid rgba($border-color, 0.1);
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
            }
          }

          .currency-details {
            .currency-name {
              font-size: 1.125rem;
              font-weight: 700;
              color: $headings-color;
              margin-bottom: 0.25rem;
              letter-spacing: 0.025em;
            }

            .currency-total {
              font-size: 1.25rem;
              font-weight: 600;
              color: $primary;
              font-family: 'Courier New', monospace;
            }
          }
        }

        .card-status {
          .status-badge {
            font-size: 0.75rem;
            font-weight: 600;
            padding: 0.375rem 0.75rem;
            border-radius: 20px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
        }
      }

      .card-content-modern {
        padding: 1.5rem;
      }

      .inactive-state {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1rem;
        background: rgba($warning, 0.05);
    border-radius: 12px;
        border: 1px solid rgba($warning, 0.1);

        .inactive-icon {
          width: 40px;
          height: 40px;
          background: rgba($warning, 0.1);
          border-radius: 50%;
        display: flex;
        align-items: center;
          justify-content: center;
          color: $warning;
          flex-shrink: 0;
        }

        .inactive-text {
          flex: 1;

          .inactive-title {
            font-size: 0.95rem;
        font-weight: 600;
        color: $headings-color;
            margin-bottom: 0.25rem;
          }

          .inactive-description {
            font-size: 0.8125rem;
            color: $text-muted;
            line-height: 1.3;
            margin: 0;
          }
        }
      }

      .active-state {
        .metrics-compact {
          display: grid;
          grid-template-columns: 1fr 1fr;
      gap: 1rem;

          @media (max-width: 576px) {
            grid-template-columns: 1fr;
          }

          .metric-compact {
            background: rgba($body-color, 0.02);
            border: 1px solid rgba($border-color, 0.08);
            border-radius: 10px;
            padding: 1rem;
            transition: all 0.2s ease;

      &:hover {
              background: rgba($primary, 0.03);
              border-color: rgba($primary, 0.15);
      }

            .metric-header {
        display: flex;
        align-items: center;
              gap: 0.5rem;
              margin-bottom: 0.75rem;

              .feather {
            color: $text-muted;
              }

              .metric-name {
                font-size: 0.8125rem;
                font-weight: 500;
                color: $text-muted;
                text-transform: uppercase;
                letter-spacing: 0.3px;
              }
            }

            .metric-data {
              .metric-value {
                display: block;
                font-size: 1.125rem;
            font-weight: 700;
            color: $headings-color;
                margin-bottom: 0.5rem;
                font-family: 'Courier New', monospace;

                &.metric-value-total {
                  font-size: 1.25rem;
                  color: $primary;
                }
              }

              .metric-progress {
                height: 4px;
                background: rgba($border-color, 0.15);
                border-radius: 2px;
                overflow: hidden;

                .progress-bar {
                  height: 100%;
                  border-radius: 2px;
                  transition: width 0.3s ease;

                  &.operation-progress {
                    background: linear-gradient(90deg, #17a2b8, #138496);
                  }

                  &.available-progress {
                    background: linear-gradient(90deg, #28a745, #20c997);
                  }

                  &.used-progress {
                    background: linear-gradient(90deg, #ffc107, #fd7e14);
                  }

                  &.total-progress {
                    background: linear-gradient(90deg, #3b82f6, #2563eb);
                  }
                }
              }
            }

            // Cores específicas por tipo de métrica
            &.metric-operation {
              .metric-header .feather { color: #17a2b8; }
            }

            &.metric-available {
              .metric-header .feather { color: #28a745; }
            }

            &.metric-used {
              .metric-header .feather { color: #ffc107; }
            }

            &.metric-total {
              .metric-header .feather { color: $primary; }
            }
          }
        }
      }
    }

    .empty-state {
              display: flex;
      flex-direction: column;
              align-items: center;
              justify-content: center;
      padding: 4rem 2rem;
      text-align: center;

      .empty-icon {
        width: 80px;
        height: 80px;
        background: rgba($text-muted, 0.1);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: $text-muted;
        margin-bottom: 1.5rem;
      }

      .empty-content {
        .empty-title {
          font-size: 1.25rem;
                font-weight: 600;
                color: $headings-color;
          margin-bottom: 0.75rem;
              }

        .empty-description {
          font-size: 0.95rem;
                color: $text-muted;
          max-width: 400px;
          line-height: 1.5;
          margin: 0;
        }
      }
    }
  }

  // Configuração Card
  .configuracao-card {
    box-shadow: 0 4px 25px rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    border: none;

    .card-header {
      background: $white;
      border-bottom: 1px solid rgba($border-color, 0.5);
      border-radius: 12px 12px 0 0;
      padding: 1.5rem;

      .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 1.5rem;

        .header-title {
          display: flex;
          align-items: center;
          gap: 1rem;

          .title-text {
            font-size: 1.25rem;
            font-weight: 600;
            color: $headings-color;
            margin-bottom: 0.25rem;
          }

          .title-subtitle {
            font-size: 0.875rem;
            color: $text-muted;
            font-weight: 500;
          }
        }

        // .header-badge {
        //   Estilos customizados removidos - usando estilo padrão do layout
        // }

        @media (max-width: 768px) {
          flex-direction: column;
          align-items: flex-start;
          gap: 1rem;
        }
      }

      .card-title {
        display: flex;
        align-items: center;
        font-size: 1.25rem;
        font-weight: 600;
        color: $headings-color;
      }
    }

    .config-section {
      background: rgba($primary, 0.02);
      border: 1px solid rgba($border-color, 0.15);
      border-radius: 8px;
      margin-bottom: 1.5rem;
      margin-top: 1.5rem;
      overflow: hidden;

      .section-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 1rem 1.25rem;
        background: rgba($primary, 0.03);
        border-bottom: 1px solid rgba($border-color, 0.1);

        .section-icon {
          width: 24px;
          height: 24px;
          background: rgba($primary, 0.08);
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: $primary;
          flex-shrink: 0;
        }

        .section-title {
          font-size: 1rem;
          font-weight: 500;
          color: $headings-color;
          margin-bottom: 0.125rem;
        }

        .section-subtitle {
          font-size: 0.8125rem;
          color: $text-muted;
          margin-bottom: 0;
        }
      }

      .config-content {
        padding: 1.25rem;

        .advanced-options-section {
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid rgba($border-color, 0.1);

          .app-collapse {
            .collapse-header {
              background: rgba($primary, 0.02);
              border: 1px solid rgba($border-color, 0.1);
              border-radius: 6px;
              padding: 0.75rem 1rem;
              font-weight: 500;
              color: $headings-color;
              transition: all 0.2s ease;

              &:hover {
                background: rgba($primary, 0.04);
                border-color: rgba($primary, 0.2);
              }
            }

            .collapse-content {
              padding: 1rem;
              background: rgba($primary, 0.01);
              border: 1px solid rgba($border-color, 0.08);
              border-top: none;
              border-radius: 0 0 6px 6px;
            }
          }
        }
      }
    }

    .status-radio-group {
      display: flex;
      gap: 1rem;
      flex-wrap: wrap;

      .status-radio {
        flex: 1;
        min-width: 200px;

        .custom-control-label {
          width: 100%;
          padding: 0;
          margin: 0;

          &::before,
          &::after {
            display: none;
          }
        }

        .radio-content {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          border: 2px solid rgba($border-color, 0.3);
          border-radius: 10px;
          background: $white;
          cursor: pointer;
          transition: all 0.3s ease;

          &:hover {
            border-color: $primary;
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          }

          // Estilos para tema escuro
          .dark-layout & {
            background: $theme-dark-card-bg;
            border-color: rgba($theme-dark-border-color, 0.3);
            color: $theme-dark-body-color;

            &:hover {
              border-color: $primary;
              box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
            }
          }

          .radio-icon {
            width: 24px;
            height: 24px;

            &.success {
              color: $success;
            }

            &.danger {
              color: $danger;
            }
          }

          span {
            font-size: 0.95rem;
            font-weight: 500;
            color: $body-color;

            .dark-layout & {
              color: $theme-dark-body-color;
            }
          }
        }

        &.custom-control-success {
          input:checked ~ .custom-control-label .radio-content {
            border-color: $success;
            background: linear-gradient(135deg, rgba($success, 0.1) 0%, rgba($success, 0.05) 100%);
            box-shadow: 0 4px 15px rgba($success, 0.2);

            .dark-layout & {
              background: linear-gradient(135deg, rgba($success, 0.2) 0%, rgba($success, 0.1) 100%);
              box-shadow: 0 4px 15px rgba($success, 0.3);
            }
          }
        }

        &.custom-control-danger {
          input:checked ~ .custom-control-label .radio-content {
            border-color: $danger;
            background: linear-gradient(135deg, rgba($danger, 0.1) 0%, rgba($danger, 0.05) 100%);
            box-shadow: 0 4px 15px rgba($danger, 0.2);

            .dark-layout & {
              background: linear-gradient(135deg, rgba($danger, 0.2) 0%, rgba($danger, 0.1) 100%);
              box-shadow: 0 4px 15px rgba($danger, 0.3);
            }
          }
        }
      }
    }

    // Config Actions
    .config-actions {
      display: flex;
      justify-content: center;
      padding: 2rem;
      border-top: 1px solid rgba($border-color, 0.3);
      margin-top: 2rem;

      .save-button {
        min-width: 200px;
        padding: 0.75rem 2rem;
        border-radius: 10px;
        font-weight: 600;
        box-shadow: 0 4px 15px rgba($primary, 0.3);
        transition: all 0.3s ease;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba($primary, 0.4);
        }
      }
    }
  }

  /* Exchange Selector - Padronizado com Dashboard de Saldos */
  .exchange-selector-container {
    box-shadow: 0 4px 25px rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    border: none;
    overflow: hidden;
    
    .dark-layout & {
      box-shadow: 0 4px 25px rgba(0, 0, 0, 0.2);
    }
    
    .exchange-selector-header {
      background: $white;
      border-bottom: 1px solid rgba($border-color, 0.5);
      border-radius: 12px 12px 0 0;
      padding: 1.5rem;
      
      .dark-layout & {
        background: $theme-dark-card-bg;
        border-bottom-color: rgba($theme-dark-border-color, 0.5);
      }
      
      .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 1.5rem;

        .header-title {
          display: flex;
          align-items: center;
          gap: 1rem;

          .title-text {
            font-size: 1.25rem;
            font-weight: 600;
            color: $headings-color;
            margin-bottom: 0.25rem;
          }

          .title-subtitle {
            font-size: 0.875rem;
            color: $text-muted;
            font-weight: 500;
          }
        }

        @media (max-width: 768px) {
          flex-direction: column;
          align-items: flex-start;
          gap: 1rem;
        }
      }
      
      .dark-layout & {
        .header-content {
          .header-title {
            .title-text {
              color: $theme-dark-headings-color;
            }
            
            .title-subtitle {
              color: $theme-dark-text-muted-color;
            }
          }
        }
      }
    }
    
    .exchange-selector-content {
      padding: 2rem;
      
      .exchange-pills {
        display: flex;
        gap: 0.75rem;
        flex-wrap: wrap;
        
        .exchange-pill {
          position: relative;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.875rem 1.25rem;
          background: rgba($body-color, 0.02);
          border: 1.5px solid rgba($body-color, 0.08);
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          font-size: 0.875rem;
          color: $body-color;
          min-width: 140px;
          
          .dark-layout & {
            background: rgba($theme-dark-body-color, 0.03);
            border-color: rgba($theme-dark-body-color, 0.12);
        color: $theme-dark-body-color;
          }

          &:hover:not(.active) {
            background: rgba($primary, 0.06);
            border-color: rgba($primary, 0.2);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba($primary, 0.1);
            
            .dark-layout & {
              background: rgba($primary, 0.08);
              box-shadow: 0 4px 12px rgba($primary, 0.15);
            }
          }

          &.active {
            background: linear-gradient(135deg, var(--pill-color-start, $primary) 0%, var(--pill-color-end, lighten($primary, 8%)) 100%);
            border-color: var(--pill-color-start, $primary);
            color: $white;
            transform: translateY(-1px);
            box-shadow: 0 6px 20px rgba($primary, 0.25);
            
            .pill-info {
              .pill-text {
                color: $white;
              }
            }

            .pill-logo {
              filter: brightness(0) invert(1);
            }
            
            .dark-layout & {
              box-shadow: 0 6px 20px rgba($primary, 0.2);
            }
          }

          .pill-logo {
            width: 24px;
            height: 24px;
            object-fit: contain;
            flex-shrink: 0;
            border-radius: 4px;
            transition: filter 0.3s ease;
            
            @media (max-width: 768px) {
              width: 22px;
              height: 22px;
            }
          }
          
          .pill-info {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            flex: 1;
            min-width: 0;
            
            .pill-text {
              font-weight: 600;
              font-size: 0.875rem;
              line-height: 1.2;
              margin-bottom: 0.125rem;
              letter-spacing: 0.025em;
            }
          }

          .active-indicator {
            position: absolute;
            top: -2px;
            right: -2px;
            width: 8px;
            height: 8px;
            background: $success;
            border: 2px solid $white;
            border-radius: 50%;
            animation: pulse-small 2s infinite;
            
            .dark-layout & {
              border-color: $theme-dark-card-bg;
            }
          }

          .pill-spinner {
            margin-left: 0.5rem;
            flex-shrink: 0;
          }
          
          /* Mobile adjustments */
          @media (max-width: 576px) {
            min-width: 120px;
            padding: 0.75rem 1rem;
            gap: 0.5rem;
            
            .pill-info {
              .pill-text {
                font-size: 0.8125rem;
              }
              
            }
          }
        }
      }
    }
  }

  /* Pulse animations */
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba($success, 0.7);
    }
    70% {
      box-shadow: 0 0 0 6px rgba($success, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba($success, 0);
    }
  }
  
  @keyframes pulse-small {
    0% {
      box-shadow: 0 0 0 0 rgba($success, 0.6);
    }
    70% {
      box-shadow: 0 0 0 4px rgba($success, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba($success, 0);
    }
  }
}

.dark-layout {
  .meu-agente-operacao {

    .saldo-dashboard-card,
    .configuracao-card {
      .dashboard-header,
      .card-header {
        background: $theme-dark-card-bg;
        border-bottom-color: rgba($theme-dark-border-color, 0.5);

        .header-title {
          .title-text {
            color: $theme-dark-headings-color;
          }

          .title-subtitle {
            color: $theme-dark-text-muted-color;
          }
        }

        // .header-badge {
        //   Estilos customizados removidos - usando estilo padrão do layout
        // }
      }
    }

    .saldo-card-modern,
    .config-section {
      background: $theme-dark-card-bg;
      border-color: rgba($theme-dark-border-color, 0.3);

      .section-header {
        background: rgba($primary, 0.05);
        border-bottom-color: rgba($theme-dark-border-color, 0.1);

        .section-title {
          color: $theme-dark-headings-color;
        }

        .section-subtitle {
          color: rgba($theme-dark-body-color, 0.7);
        }
      }

      .config-content {
        .advanced-options-section {
          border-top-color: rgba($theme-dark-border-color, 0.2);

          .app-collapse {
            .collapse-header {
              background: rgba($primary, 0.05);
              border-color: rgba($theme-dark-border-color, 0.2);
              color: $theme-dark-headings-color;

              &:hover {
                background: rgba($primary, 0.08);
                border-color: rgba($primary, 0.3);
              }
            }

            .collapse-content {
              background: rgba($primary, 0.02);
              border-color: rgba($theme-dark-border-color, 0.15);
            }
          }
        }
      }

      .card-header-modern {
        background: rgba($theme-dark-body-color, 0.03);
        border-bottom-color: rgba($theme-dark-border-color, 0.12);

        .currency-info {
          .currency-details {
            .currency-name {
              color: $theme-dark-headings-color;
            }

            .currency-total {
              color: $primary;
            }
          }
        }
      }

      .inactive-state {
        background: rgba($warning, 0.08);
        border-color: rgba($warning, 0.15);

        .inactive-icon {
          background: rgba($warning, 0.15);
        }

        .inactive-text {
          .inactive-title {
            color: $theme-dark-headings-color;
          }

          .inactive-description {
            color: $theme-dark-text-muted-color;
          }
        }
      }

      .active-state {
        .metrics-compact {
          .metric-compact {
            background: rgba($theme-dark-body-color, 0.04);
            border-color: rgba($theme-dark-border-color, 0.12);

            &:hover {
              background: rgba($primary, 0.05);
              border-color: rgba($primary, 0.2);
            }

            .metric-header {
              .feather {
                color: $theme-dark-text-muted-color;
              }

              .metric-name {
                color: $theme-dark-text-muted-color;
              }
            }

            .metric-data {
              .metric-value {
                color: $theme-dark-headings-color;

                &.metric-value-total {
                  color: $primary;
                }
              }

              .metric-progress {
                background: rgba($theme-dark-border-color, 0.2);
              }
            }
          }
        }
      }

      .section-header {
        background: linear-gradient(135deg, rgba($primary, 0.1) 0%, rgba($primary, 0.05) 100%);
        border-bottom-color: rgba($theme-dark-border-color, 0.3);
      }
    }

    .empty-state {
      .empty-icon {
        background: rgba($theme-dark-text-muted-color, 0.1);
        color: $theme-dark-text-muted-color;
      }

      .empty-content {
        .empty-title {
          color: $theme-dark-headings-color;
        }

        .empty-description {
          color: $theme-dark-text-muted-color;
        }
      }
    }

    .exchange-pill {
      background: $theme-dark-card-bg;
      border-color: rgba($theme-dark-border-color, 0.3);

      .pill-text {
        color: $theme-dark-body-color;
      }
    }

    .status-radio .radio-content {
      background: $theme-dark-card-bg;
      border-color: rgba($theme-dark-border-color, 0.3);

      span {
        color: $theme-dark-body-color;
      }
    }
  }

  div ::v-deep {
      .b-overlay {
        .bg-light {
          background-color: $theme-dark-body-bg !important;
        }
      }
  }

  // Instrução das abas
  .tab-instruction {
    margin-top: 0.5rem;
    padding: 0.75rem 1rem;
    background: rgba($primary, 0.05);
    border-radius: 6px;
    border-left: 3px solid $primary;
    
    small {
      display: flex;
      align-items: center;
      font-weight: 500;
      
      i {
        color: $primary;
        animation: pulse 2s infinite;
      }
    }
  }

  // Estilos para melhorar usabilidade das abas de moedas
  .currency-tabs {
    .currency-tab-item {
      position: relative;
      
      .currency-tab-link {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        padding: 1rem 1.5rem;
        font-weight: 600;
        font-size: 1rem;
        color: $text-muted;
        border: 2px solid transparent;
        border-radius: 8px 8px 0 0;
        transition: all 0.3s ease;
        cursor: pointer;
        background: rgba($primary, 0.05);
        
        &:hover {
          color: $primary;
          background: rgba($primary, 0.1);
          border-color: rgba($primary, 0.3);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba($primary, 0.2);
        }
        
        &:before {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 3px;
          background: $primary;
          transition: width 0.3s ease;
        }
        
        &.active {
          color: $primary;
          background: $white;
          border-color: $primary;
          border-bottom-color: $white;
          
          &:before {
            width: 100%;
          }
        }
      }
    }
    
    // Adicionar ícones nas abas
    .nav-link {
      &[aria-controls*="USDT"] {
        &:before {
          content: '💵';
          margin-right: 0.5rem;
          font-size: 1.2rem;
        }
      }
      
      &[aria-controls*="USDC"] {
        &:before {
          content: '💳';
          margin-right: 0.5rem;
          font-size: 1.2rem;
        }
      }
      
      &[aria-controls*="Bitcoin"] {
        &:before {
          content: '₿';
          margin-right: 0.5rem;
          font-size: 1.2rem;
          font-weight: bold;
        }
      }
    }
  }

  // Animação para o ícone de instrução
  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }
}
</style>